import { Modal as UniModal, buttonClasses } from '@planne-software/uni';
import { Box, styled, stackClasses, radioClasses } from '@planne-software/uni/mui/material';
import { uni } from '@planne-software/uni/v2';

import { breakpoints } from '@src/common/breakpoints';
import { tabletBreakpoint } from '@src/common/utils/mediaQuery';
import { uniTypography } from '@src/fonts/typography/uniTypography';

export const FieldStack = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
  width: '100%',
}));

/** Two columns on desktop, one on phones — the layout every field pair in the Figma uses. */
export const FieldGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: uni('scale-400'),
  width: '100%',
  [tabletBreakpoint]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
}));

export const FieldSpan = styled(Box)(() => ({
  gridColumn: '1 / -1',
  minWidth: 0,
}));

export const FieldHint = styled('span')`
  ${uniTypography('text-sm', 'regular')}
  color: ${uni('fg-neutral-static-default-caption')};
`;

export const IdentityTypeGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: uni('scale-400'),
  width: '100%',
}));

export const IdentityTypeCard = styled('label')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: uni('scale-400'),
  minHeight: '120px',
  padding: uni('padding-card-components'),
  borderRadius: uni('border-radius-xs'),
  border: `1px solid ${uni('border-neutral-subtle-rest')}`,
  backgroundColor: uni('bg-neutral-default-rest'),
  cursor: 'pointer',
  minWidth: 0,
  '&[data-selected="true"]': {
    borderColor: uni('border-primary-default-rest'),
    backgroundColor: uni('bg-primary-subtle-rest'),
  },
}));

export const IdentityTypeCardHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
  gap: uni('scale-200'),
}));

export const IdentityTypeCardIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.5rem',
  height: '2.5rem',
  flexShrink: 0,
  borderRadius: uni('border-radius-round'),
  backgroundColor: uni('fg-neutral-action-default-rest'),
  color: uni('neutral-white'),
  '&[data-selected="true"]': {
    backgroundColor: uni('bg-primary-default-rest'),
    color: uni('neutral-white'),
  },
}));

export const IdentityTypeCardRadio = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexShrink: 0,
  marginLeft: 'auto',

  [`& .${radioClasses.root}`]: {
    padding: 0,
    color: uni('border-neutral-subtle-rest'),
  },

  [`& .${radioClasses.root}.Mui-checked`]: {
    color: uni('bg-primary-default-rest'),
  },
}));

export const IdentityTypeCardText = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-50'),
  minWidth: 0,
}));

export const IdentityTypeCardTitle = styled('span')`
  ${uniTypography('text-md', 'semibold')}
  color: ${uni('fg-neutral-action-default-rest')};

  &[data-selected='true'] {
    color: ${uni('fg-primary-default-rest')};
  }
`;

export const IdentityTypeCardSubtitle = styled('span')`
  ${uniTypography('text-sm', 'regular')}
  color: ${uni('fg-neutral-static-default-caption')};

  &[data-selected='true'] {
    color: ${uni('fg-primary-on-color-subtle-rest')};
  }
`;

export const ZipCodeInputWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-100'),
  width: '100%',
}));

export const InputHintRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: uni('scale-100'),
  color: uni('fg-neutral-static-default-caption'),

  '& svg': {
    flexShrink: 0,
    marginTop: '2px',
  },
}));

export const InputHintText = styled('span')`
  ${uniTypography('text-sm', 'regular')}
  color: ${uni('fg-neutral-static-default-caption')};
`;

export const InlineOptionsRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${uni('scale-400')};
  width: 100%;
  ${uniTypography('text-xs', 'semibold')}
`;

export const FieldWithLinkStack = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: uni('scale-100'),
  width: '100%',
}));

export const TermsStack = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

export const TermsRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',

  [`@media (max-width: ${breakpoints.smallTablet})`]: {
    alignItems: 'flex-start',
  },
}));

export const TermsCheckboxSlot = styled(Box)(() => ({
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  paddingTop: uni('scale-50'),
}));

export const TermsText = styled('div')`
  display: inline-flex;
  gap: ${uni('scale-150')};
  flex-wrap: wrap;
  color: ${uni('fg-neutral-static-default-body')};
  ${uniTypography('text-sm', 'regular')}
`;

export const CheckboxAlignRow = styled(Box)`
  display: flex;
  align-items: center;
  width: 100%;

  .${stackClasses.root} {
    align-items: center;

    p {
      font-family: ${uni('font-family-text')};
      ${uniTypography('text-sm', 'semibold')};
      color: ${uni('fg-neutral-action-default-rest')};
    }
  }
`;

export const FieldErrorText = styled('span')`
  ${uniTypography('text-sm', 'regular')}
  color: ${uni('fg-alert-default-rest')};
`;

export const SubmitFooter = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: uni('scale-400'),
  width: '100%',
  [tabletBreakpoint]: {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',

    [`& .${buttonClasses.root}`]: {
      width: '100%',
    },
  },
}));

export const SecureFooter = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-100'),
  flex: '1 1 auto',
  minWidth: 0,
  color: uni('fg-success-default-rest'),
}));

export const SecureFooterText = styled('span')`
  ${uniTypography('text-md', 'medium')}
  color: ${uni('fg-success-default-rest')};
`;

export const SubmitButtonWrapper = styled(Box)(() => ({
  flex: '0 0 auto',

  [tabletBreakpoint]: {
    width: '100%',
    minWidth: 0,
  },
}));

export const RecaptchaWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: uni('scale-100'),
  width: '100%',
}));

export const PasswordSuffixButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: ${uni('scale-250')};
  cursor: pointer;
  color: ${uni('fg-neutral-action-default-rest')};
`;

export const ZipCodeHelpContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderRadius: uni('border-radius-md'),
  backgroundColor: uni('bg-static-content-default'),
  outline: 'none',
}));

export const ZipCodeHelpBody = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: uni('scale-400'),
}));

export const TermsModalContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderRadius: uni('border-radius-md'),
  backgroundColor: uni('bg-static-content-default'),
  outline: 'none',
}));

export const TermsModalBody = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: uni('scale-400'),
  maxHeight: 'min(32rem, 70vh)',
  overflowY: 'auto',
}));

export const ZipCodeHelpText = styled('p')`
  display: inline-flex;
  flex-wrap: wrap;
  ${uniTypography('text-md', 'regular')}
  color: ${uni('fg-neutral-static-default-body')};
  margin: 0;
`;

export const Modal = styled(UniModal)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: 'auto 0 0',
  zIndex: theme.zIndex.modal + 9999,

  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    maxWidth: '47.5rem',
    margin: '0 auto',
  },
}));
